// feature/monthly-report — monthly report dashboard at /admin/reports/monthly.
// Five sections: Casos / Insumos / Auditoría / Performance del equipo /
// Performance de insumos. Each section has its own CSV download.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiBase } from "../../api/client";
import Spinner from "../../components/Spinner";
import { useToast } from "../../components/Toast";

const TIPO_LABEL = {
  total: "Total",
  tutela: "Tutelas",
  fallo: "Fallos",
  requerimiento: "Requerimientos",
  desacato: "Desacatos",
  sancion: "Sanciones",
};

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// feature/monthly-report-r2 — arrow guard. Suppress the "vs mes anterior"
// indicator entirely when there's no prior-month baseline to compare
// against (b is 0, null, or missing). Without this, the dashboard
// rendered "↑ 298 vs mes anterior" for tutela in May 2026 even though
// April was genuinely empty (platform launched 2026-05-01) — visually
// indistinguishable from real growth, and the same misinformation will
// recur for any new metric that starts being tracked mid-flow.
function delta(a, b) {
  if (b == null || b === 0) return null;
  const d = a - b;
  if (d === 0) return null;
  return { value: d, sign: d > 0 ? "up" : "down" };
}

export default function MonthlyReport() {
  const toast = useToast();
  const navigate = useNavigate();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  function load() {
    setBusy(true);
    api.get(`/api/admin/reports/monthly?month=${month}`)
      .then(r => setData(r.data))
      .catch(() => toast.push("Error al cargar el reporte", "error"))
      .finally(() => setBusy(false));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  async function downloadCsv(section) {
    const token = localStorage.getItem("eds.token");
    try {
      const r = await fetch(`${apiBase}/api/admin/reports/monthly.csv?section=${section}&month=${month}`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!r.ok) throw new Error("HTTP " + r.status);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `reporte_${section}_${month}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.push("Error al descargar CSV", "error");
    }
  }

  return (
    <div className="col" style={{ gap: 24 }}>
      <div className="row">
        <h1 style={{ margin: 0 }}>Reporte mensual</h1>
        <div style={{ flex: 1 }} />
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value || currentMonth())} style={{ width: "auto" }} />
      </div>
      <div className="muted" style={{ marginTop: -16 }}>
        Resumen ejecutivo del mes — casos ingresados, insumos, auditoría y desempeño por miembro.
      </div>

      {data == null ? (
        <div className="card" style={{ padding: 48, textAlign: "center" }}><Spinner size={20} /></div>
      ) : (
        <>
          <Section title="Casos del mes" onCsv={() => downloadCsv("casos")}>
            <div className="grid-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))" }}>
              {["total","tutela","fallo","requerimiento","desacato","sancion"].map(t => (
                <StatCard
                  key={t}
                  label={TIPO_LABEL[t]}
                  value={data.casos[t].this_month}
                  delta={delta(data.casos[t].this_month, data.casos[t].prev_month)}
                  primary={t === "total"}
                />
              ))}
            </div>
          </Section>

          {/* feature/vencidos-visibility-monthly-report — scope: cases with
              fecha_ingreso in the selected month that are currently vencidos
              (kind=bad) or por vencer ≤4h (kind=warn) per lib/sla.js. Click
              jumps to /admin/sla-breaches for the live unscoped view. */}
          <Section title="Vencimientos del mes" onCsv={() => downloadCsv("vencimientos")}>
            <div className="grid-3">
              <StatCard label="Vencidos"
                        value={data.vencimientos?.vencidos ?? 0}
                        tone={(data.vencimientos?.vencidos ?? 0) > 0 ? "danger" : null}
                        onClick={() => navigate("/admin/sla-breaches")}
                        help="Casos con fecha_ingreso en este mes que actualmente están vencidos (SLA en rojo). Excluye contestadas, archivadas y estados terminales." />
              <StatCard label="Por vencer"
                        value={data.vencimientos?.por_vencer ?? 0}
                        tone={(data.vencimientos?.por_vencer ?? 0) > 0 ? "warning" : null}
                        onClick={() => navigate("/admin/sla-breaches")}
                        help="Casos con fecha_ingreso en este mes con menos de 4 horas restantes de SLA." />
            </div>
          </Section>

          <Section title="Insumos" onCsv={() => downloadCsv("insumos")}>
            <div className="grid-3">
              <StatCard label="Solicitados" value={data.insumos.solicitados} />
            </div>
          </Section>

          <Section title="Actividad de auditoría" onCsv={() => downloadCsv("auditoria")}>
            <div className="grid-4">
              <StatCard label="Enviados"   value={data.auditoria.enviados}
                        help="Eventos send_audit del mes (audit_log.action = 'send_audit'). Una transición; cada caso puede aparecer más de una vez si fue reenviado." />
              <StatCard label="Aprobados"  value={data.auditoria.aprobados} tone="success"
                        help="Decisiones de aprobación de auditoría (audit_reviews.result = 'approve'). Mide transiciones, no estado final — un caso puede llegar a 'En Litisoft' sin pasar por aquí (override admin)." />
              <StatCard label="Devueltos"  value={data.auditoria.devueltos} tone={data.auditoria.devueltos ? "warning" : null}
                        help="Decisiones de rechazo de auditoría (audit_reviews.result = 'reject') del mes." />
              <StatCard label="Tasa de rechazo" value={`${data.auditoria.tasa_rechazo}%`}
                         tone={data.auditoria.tasa_rechazo > 30 ? "danger" : null}
                         help="devueltos / (aprobados + devueltos). 0% si no hay decisiones en el mes." />
            </div>
          </Section>

          <Section title="Desempeño del equipo" onCsv={() => downloadCsv("equipo")}>
            <SortableTable
              columns={[
                { key: "name",            label: "Nombre" },
                { key: "asignados",       label: "Asignados",      align: "right",
                  help: "Casos con fecha_ingreso en el mes y abogado asignado al usuario." },
                { key: "completados",     label: "Completados",    align: "right",
                  help: "Casos del usuario que entraron a 'En Litisoft' o 'Cerrado' este mes (snapshot por updated_at — no necesariamente aprobados por auditoría; la columna 'Aprobados' mide transiciones, no estado final)." },
                { key: "avg_horas_envio", label: "Tiempo prom envío (h)", align: "right",
                  help: "Promedio de horas entre fecha_ingreso y el primer send_audit del caso, solo casos con primer envío en el mes.",
                  render: v => v == null ? "—" : v.toFixed(1) },
                { key: "vencidos",        label: "Atrasados", align: "right",
                  help: "Casos del usuario con fecha_ingreso en este mes que actualmente están vencidos (SLA en rojo). Color rojo cuando > 0.",
                  render: v => {
                    const n = v ?? 0;
                    return n > 0
                      ? <span style={{ color: "var(--color-danger)", fontWeight: 600 }}>{n}</span>
                      : <span className="muted">{n}</span>;
                  }},
                { key: "tasa_rechazo",    label: "Tasa rechazo",   align: "right",
                  help: "rejects / (approve + reject) sobre las decisiones de auditoría del mes para los casos del usuario.",
                  render: v => `${v}%` },
              ]}
              rows={data.equipo}
              empty="Sin datos del equipo este mes."
            />
          </Section>

          <Section title="Desempeño de insumos" onCsv={() => downloadCsv("insumos_team")}>
            <SortableTable
              columns={[
                { key: "name",              label: "Nombre" },
                { key: "asignados",         label: "Casos asignados",  align: "right" },
                { key: "solicitudes",       label: "Solicitudes",      align: "right" },
              ]}
              rows={data.insumos_team}
              empty="Sin datos de insumos este mes."
            />
          </Section>
        </>
      )}
      {busy && <div className="muted" style={{ fontSize: 12 }}>Actualizando…</div>}
    </div>
  );
}

function Section({ title, onCsv, children }) {
  return (
    <section className="col" style={{ gap: 12 }}>
      <div className="row" style={{ alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
        <div style={{ flex: 1 }} />
        <button className="ghost sm" onClick={onCsv}>Descargar CSV</button>
      </div>
      {children}
    </section>
  );
}

function StatCard({ label, value, delta, tone, primary, help, onClick }) {
  const color = tone === "danger"  ? "var(--color-danger)"
              : tone === "warning" ? "var(--color-warning)"
              : tone === "success" ? "var(--color-success)"
              : "var(--text-primary)";
  return (
    <div className={"card tight" + (onClick ? " clickable" : "")}
         onClick={onClick}
         style={onClick ? { cursor: "pointer" } : undefined}>
      <div className="label-uc" title={help || undefined} style={help ? { cursor: "help" } : undefined}>{label}</div>
      <div style={{ fontSize: primary ? 36 : 28, fontWeight: 700, color, marginTop: 6 }}>{value}</div>
      {delta && (
        <div style={{ fontSize: 11, marginTop: 4, color: delta.sign === "up" ? "var(--accent-mint)" : "var(--color-danger)" }}>
          {delta.sign === "up" ? "↑" : "↓"} {Math.abs(delta.value)} vs mes anterior
        </div>
      )}
    </div>
  );
}

function SortableTable({ columns, rows, empty }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "es") * dir;
    });
  }, [rows, sortKey, sortDir]);

  function toggle(k) {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  }

  if (!rows.length) {
    return <div className="card tight muted" style={{ textAlign: "center", padding: 24 }}>{empty}</div>;
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      <table>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key} onClick={() => toggle(c.key)}
                  title={c.help || undefined}
                  style={{ cursor: c.help ? "help" : "pointer", textAlign: c.align || "left" }}>
                {c.label} {sortKey === c.key ? (sortDir === "asc" ? "▲" : "▼") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={r.id ?? i}>
              {columns.map(c => (
                <td key={c.key} style={{ textAlign: c.align || "left" }}>
                  {c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
